import { useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { Sheet, SheetClose, SheetContent } from "@/lib/components/Sheet";
import { Button } from "@/lib/components/Button/Button";
import { IconX } from "@tabler/icons-react";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import AnimationView from "./AnimationView";
import PromptLibraryView from "./PromptLibraryView";
import {
  MainViewState,
  PresenceData,
  SidebarViewState,
  useViewHistory,
  useViewTransitionProps,
} from "./creative-project-hooks";
import CreativeElementsView from "./CreativeElementsView/CreativeElementsView";
import EditImageView from "./EditImageView";
import InspirationGrid from "./Inspiration/InspirationGrid";
import InspirationGuide from "./Inspiration/InspirationGuide";
import { INSPIRATION_GUIDES } from "./Inspiration/inspiration-guides";

export default function CreativeProjectSheet() {
  const isSheetOpen = useEditorStore(
    (state) => state.creativeProjectSheetState.isSheetOpen,
  );
  const currentProjectExternalId = useEditorStore(
    (state) => state.creativeProjectSheetState.currentCreativeProjectExternalId,
  );
  const closeCreativeProjectSheet = useEditorStore(
    (state) => state.service.closeCreativeProjectSheet,
  );

  useEffect(() => {
    if (isSheetOpen) {
      AnalyticsApi.logCreativeModeOpened();
    }
  }, [isSheetOpen]);

  return (
    <Sheet
      open={isSheetOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeCreativeProjectSheet();
        }
      }}
    >
      <SheetContent
        key={currentProjectExternalId}
        data-slot="creative-project-sheet"
        className="w-[calc(100%-64px)] max-w-360 rounded-l-2xl border-0 border-none sm:max-w-360"
        side="right"
        closeButton={false}
      >
        <SheetClose asChild>
          <Button
            variant="outline"
            size="icon"
            className="absolute top-3 -left-13.5 z-100 border-none"
          >
            <IconX className="size-5 text-text-primary" />
            <span className="sr-only">Close</span>
          </Button>
        </SheetClose>

        <CreativeProjectSheetContent />
      </SheetContent>
    </Sheet>
  );
}

function CreativeProjectSheetContent() {
  const {
    view: sidebarView,
    transition: sidebarTransition,
    setView: setSidebarView,
    goBack: goBackSidebar,
  } = useViewHistory<SidebarViewState>({ mode: "LIST" });

  const selectedGuide =
    sidebarView.mode === "GUIDE"
      ? INSPIRATION_GUIDES.find((g) => g.id === sidebarView.guideId)
      : undefined;

  const {
    view: mainView,
    transition: mainTransition,
    setView: setMainView,
    goBack: goBackMainView,
    updateCurrentView: updateCurrentMainView,
  } = useViewHistory<MainViewState>({
    mode: "ANIMATION",
    sourceImageKey: "startSourceImageExternalId",
  });

  const mainViewPresenceData: PresenceData = {
    currentMode: mainView.mode,
    transition: mainTransition,
  };

  const sidebarPresenceData: PresenceData = {
    currentMode: sidebarView.mode,
    transition: sidebarTransition,
  };

  return (
    <div className="flex h-full w-full">
      <div className="relative flex-1 self-stretch overflow-hidden">
        <AnimatePresence custom={mainViewPresenceData}>
          {mainView.mode === "ANIMATION" && (
            <AnimationView
              key="ANIMATION"
              mainView={mainView}
              setMainView={setMainView}
              updateCurrentMainView={updateCurrentMainView}
            />
          )}

          {mainView.mode === "EDIT_IMAGE" && (
            <EditImageView
              key="EDIT_IMAGE"
              mainView={mainView}
              setMainView={setMainView}
              goBackMainView={goBackMainView}
              updateCurrentMainView={updateCurrentMainView}
            />
          )}

          {mainView.mode === "PROMPT_LIBRARY" && (
            <PromptLibraryView
              key="PROMPT_LIBRARY"
              mainView={mainView}
              goBackMainView={goBackMainView}
            />
          )}

          {mainView.mode === "ELEMENTS" && (
            <CreativeElementsView
              key="ELEMENTS"
              mainView={mainView}
              goBackMainView={goBackMainView}
            />
          )}
        </AnimatePresence>
      </div>

      <div
        className={cn(
          "relative shrink-0 grow-0 basis-110 self-stretch overflow-hidden bg-cover bg-center xl:basis-120",
          {
            "max-xl:hidden": ["PROMPT_LIBRARY", "ELEMENTS"].includes(
              mainView.mode,
            ),
          },
        )}
        style={{
          backgroundImage: "url(/inspiration/inspiration-background.jpeg)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-black/80"
        />
        <AnimatePresence custom={sidebarPresenceData}>
          {sidebarView.mode === "LIST" && (
            <SidebarPanel key="INSPIRATION_GRID">
              <InspirationGrid setSidebarView={setSidebarView} />
            </SidebarPanel>
          )}

          {sidebarView.mode === "GUIDE" && selectedGuide && (
            <SidebarPanel key="INSPIRATION_GUIDE">
              <InspirationGuide
                key={selectedGuide.id}
                guide={selectedGuide}
                mainView={mainView}
                onBack={() => goBackSidebar("SLIDE_FROM_LEFT")}
              />
            </SidebarPanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function SidebarPanel({ children }: { children: React.ReactNode }) {
  const transitionProps = useViewTransitionProps({ duration: 0.2 });

  return (
    <motion.div {...transitionProps} className="absolute inset-0 h-full w-full">
      {children}
    </motion.div>
  );
}
